import { BaseOrderAdapter } from "./BaseOrderAdapter.js";
import {
  UNIFIED_ORDER_STATUS,
  UNIFIED_PAYMENT_STATUS,
  UNIFIED_DELIVERY_STATUS,
  UNIFIED_ORDER_TYPE,
  UNIFIED_ORDER_STATUS_LEVEL,
  UNIFIED_ORDER_STATUS_LEVEL_MAP,
} from "../types/UnifiedOrderTypes.js";

export class ZomatoOrderAdapter extends BaseOrderAdapter {
  getPartnerId() {
    return "zomato";
  }

  getPartnerName() {
    return "Zomato";
  }

  transformOrder(orderData) {
    const order = orderData;
    let cachedOrderLevel = localStorage.getItem(`partnerOrders`);
    if (cachedOrderLevel) {
      cachedOrderLevel = JSON.parse(cachedOrderLevel);
    } else {
      cachedOrderLevel = {};
    }

    // Transform all components
    const billTxnLogs = this.transformBillTxnLogs(order);
    const items = this.transformItems(order);
    const delivery = this.transformDelivery(order);
    const customer = this.transformCustomer(order);
    const restaurant = this.transformRestaurant(order);
    const meta = this.transformOrderMeta(order);

    // Get prep time
    const prepTime = this.getPrepTime(order);

    let orderStatus = this.getUnifiedStatus(order.state);
    let orderStatusLevel = UNIFIED_ORDER_STATUS_LEVEL[orderStatus];

    if (cachedOrderLevel[order.id]) {
      if (cachedOrderLevel[order.id] > orderStatusLevel) {
        orderStatus =
          UNIFIED_ORDER_STATUS_LEVEL_MAP[cachedOrderLevel[order.id]];
        orderStatusLevel = cachedOrderLevel[order.id];
      } else {
        cachedOrderLevel[order.id] = orderStatusLevel;
        localStorage.setItem(`partnerOrders`, JSON.stringify(cachedOrderLevel));
      }
    }

    const discount = this.calculateTotalDiscount(
      order.cartDetails.discountApplied || null
    );

    let orderInstruction = `${order.instruction?.text}` || "";
    let otherInstructions = order.orderMessages
      ?.filter(
        (msg) =>
          msg.messageTag != "header_tag" && msg.messageTag != "footer_tag"
      )
      .map((msg) => msg.value?.message)
      .join(", ");
    if (otherInstructions) {
      orderInstruction += `, ${otherInstructions}`;
    }

    return {
      id: order.id,
      orderId: order.id,
      partnerId: this.getPartnerId(),
      partnerOrderId: order.id,
      partnerName: this.getPartnerName(),
      billNo: order.displayId,
      staffId: "",
      tableVPA: "NA",
      orderType: UNIFIED_ORDER_TYPE.PARTNER,
      typeOfOrder: UNIFIED_ORDER_TYPE.DELIVERY,
      orderStatus: orderStatus,
      orderStatusLevel: orderStatusLevel,
      billStatus: order.paymentDetails?.paymentType || "pending",
      amount:
        Number(order.cartDetails?.total?.amountDetails?.totalCost).toFixed(2) ||
        0,
      paymentmethod: order.paymentMethod,
      partialAmoutsplit: [],
      billTxnLogs,
      discount: discount, // Will be calculated in billTxnLogs
      tips: 0,
      orderPreparationNote: orderInstruction,
      items,
      customer,
      delivery,
      restaurant,
      meta,
      createdAt: order.createdAt,
      updatedAt: order.updatedAt,
      acceptedAt: order.actionedAt,
      canceled: order.state === "CANCELLED",
      cancelReason: "",
      orderOTP: order.otp,
      rawOrder: orderData,
      prepTime,
    };
  }

  transformBillTxnLogs(order) {
    const cartDetails = order.cartDetails;
    const subtotal = cartDetails?.subtotal?.amountDetails?.totalCost || 0;
    const total = cartDetails?.total?.amountDetails?.totalCost || 0;

    // Calculate CGST and SGST from dish calculations
    const taxes = this.calculateTotalTaxes(cartDetails.charges);

    const discount = this.calculateTotalDiscount(
      cartDetails.discountApplied || null
    );

    return {
      beforeDiscountTotal: subtotal,
      cgst: Number(taxes.cgst.toFixed(2)),
      cgstPercent: "2.5",
      discount: discount, // No discount in the sample data
      discountFlat: discount,
      discountPercent: 0,
      gst: true,
      gstExcluded: false,
      gstNo: "",
      service: false,
      sgst: Number(taxes.sgst.toFixed(2)),
      sgstPercent: "2.5",
      subTotal: subtotal,
      tip: 0,
      total: Number(total.toFixed(2)),
      paymentType: "online",
      showGSTFooter: false,
    };
  }

  calculateTotalDiscount(discountApplied) {
    if (!discountApplied) return 0;

    /*
    
      discountApplied: {
        discounts: [
          {
            discount: {
              id: "-1",
              name: "Discount",
              type: "total_merchant_voucher_and_salt_discount",
              offers: [
                {
                  type: "PERCENTAGE_DISCOUNT",
                  amount: -31.8999996,
                  offerAmount: 31.9,
                },
              ],
              totalDiscountAmount: -31.8999996,
              displayCost: "-₹31.90",
            },
            amountBreakup: {
              title: "Discount",
              body: {
                keyValues: [
                  {
                    key: {
                      message: "Promo discount",
                    },
                    value: {
                      message: "-₹31.90",
                    },
                  },
                ],
                footer: {
                  key: {
                    message: "Total discount",
                  },
                  value: {
                    message: "-₹31.90",
                  },
                },
              },
            },
          },
        ],
      },
    
    */
    const discount = discountApplied.discounts.reduce((acc, discount) => {
      return acc + discount.discount.totalDiscountAmount;
    }, 0);
    return Math.abs(discount);
  }

  calculateTotalTaxes(charges) {
    const tax = charges.find(
      (charge) =>
        charge.amountDetails.type == "tax" ||
        charge.amountDetails.itemName == "CGST"
    );

    const taxAmmount =
      (tax.amountDetails.displayCost &&
        Number(tax.amountDetails.displayCost.replace("₹", ""))) ||
      0;

    const cgst = taxAmmount / 2;
    const sgst = taxAmmount / 2;

    return { cgst, sgst };
  }

  transformItems(order) {
    return (
      order.cartDetails?.items?.dishes?.map((item) => {
        //         instruction: {
        //             id: "1",
        //             text: "Make it Extra creamy",
        //             entityId: "1",
        //             entityType: "ITEM",
        //             target: "MERCHANT",
        //             actions: [
        //               {
        //                 name: "DISH_COOKING_TEXT",
        //                 value: "Make it Extra creamy",
        //                 actionStatus: "ACTION_STATUS_SUCCESS",
        //               },
        //             ],
        //             isActive: true,
        //           }

        return {
          id: item.id,
          itemMenuId: item.referenceId,
          feUniqueItemId: `${item.id}`,
          name: item.name,
          quantity: item.quantity,
          price: Number(item.unitCost).toFixed(2),
          variantName: "",
          variantId: "",
          isVariant: false,
          isExtra: false,
          extra: false,
          itemPreparationNote: item.instruction?.text || "",
          itemCancelReason: "",
          status: "preparing",
          isCancelled: false,
          category: item.subCategoryId,
          isVeg: item.metadata?.tags?.includes("veg"),
          addons: [],
          variants: [],
          createdAt: order.createdAt,
          updatedAt: order.updatedAt,
          metadata: {
            tags: item.metadata?.tags || [],
            catalogueId: item.metadata?.catalogueId,
            refVei: item.metadata?.refVei,
            catalogueVei: item.metadata?.catalogueVei,
            unitCost: item.dishUnitCost,
            displayCost: item.displayCost,
          },
        };
      }) || []
    );
  }

  transformCustomer(order) {
    const creator = order.creator;
    return {
      id: creator?.userId,
      name: creator?.name,
      phone: creator?.countryIsdCode + creator?.userId,
      address: {
        id: creator?.address?.id,
        text: creator?.address?.address,
        instructions: creator?.address?.addressInstructions || "",
        location: {
          lat: creator?.address?.location?.latitude || 0,
          lng: creator?.address?.location?.longitude || 0,
        },
        locality: creator?.address?.locality,
      },
      orderCount: creator?.orderCount,
      orderCountDisplay: creator?.orderCountDisplay,
      profileUrl: creator?.profileUrl,
      profilePicture: creator?.profilePictureUrl,
      segment: creator?.customerSegmentation,
      originalName: creator?.originalName,
      instructions: order.instruction?.text,
    };
  }

  transformDelivery(order) {
    const rider = order.supportingRiderDetails?.[0];
    return {
      status: this.getUnifiedDeliveryStatus(rider?.riderStatus),
      eta: null,
      handoverTime: order.handoverDetails?.time,
      maxTime: order.handoverDetails?.maxTime,
      minTime: order.handoverDetails?.minTime,
      distance: this.extractDistanceFromAddress(
        order.creator?.address?.address
      ),
      person: rider
        ? {
            name: rider.name,
            phone: rider.phone,
            image: rider.image || null,
            status: rider.riderStatus,
            title: rider.title,
          }
        : null,
      mode: order.deliveryMode,
      isZomatoDelivered: order.zomatoDelivered,
      handoverDetails: order.handoverDetails,
      acceptExpiryTime: order.acceptExpiryTime,
      pickupThresholdTime: order.pickupThresholdTime,
    };
  }

  transformRestaurant(order) {
    return {
      id: order.resId,
      displayId: order.displayId,
      shouldShowKptWidget: order.shouldShowKptWidget,
      show_kot_checklist: order.show_kot_checklist,
      help_chat_config: order.help_chat_config,
      live_order_support_deeplink: order.live_order_support_deeplink,
      billPrintData: order.billPrintData,
      kotPrintData: order.kotPrintData,
    };
  }

  transformOrderMeta(order) {
    return {
      prepTimePredicted: order.handoverDetails?.time || 0,
      orderedTime: order.createdAt,
      placedTime: order.actionedAt,
      placingState: order.state,
      earlyMfrTime: 0,
      foodHandoverTimeSec: order.pickupThresholdTime || 0,
      messages: [
        ...(order.orderMessages?.map((msg) => msg.value?.message) || []),
        order.orderMessage?.message,
      ].filter(Boolean),
      nextStates: order.nextStates || [],
      alert: order.alert,
      meta: order.meta,
      orderMessage: order.orderMessage,
    };
  }

  getUnifiedStatus(status) {
    const statusMap = {
      NEW: UNIFIED_ORDER_STATUS.NEW,
      PREPARING: UNIFIED_ORDER_STATUS.PREPARING,
      READY: UNIFIED_ORDER_STATUS.READY,
      DISPATCHED: UNIFIED_ORDER_STATUS.PICKEDUP,
      MERCHANT_REJECTED: UNIFIED_ORDER_STATUS.REJECTED,
      UNDER_REVIEW: UNIFIED_ORDER_STATUS.UNDER_REVIEW,
      USER_CANCELLED: UNIFIED_ORDER_STATUS.CANCELLED,
    };
    return statusMap[status] || status;
  }

  getUnifiedDeliveryStatus(status) {
    const statusMap = {
      REQUESTED: UNIFIED_DELIVERY_STATUS.UNASSIGNED,
      ASSIGNED: UNIFIED_DELIVERY_STATUS.ASSIGNED,
      ARRIVED: UNIFIED_DELIVERY_STATUS.ARRIVED,
      PICKED_UP: UNIFIED_DELIVERY_STATUS.PICKED,
      DELIVERED: UNIFIED_DELIVERY_STATUS.DELIVERED,
      CANCELLED: UNIFIED_DELIVERY_STATUS.CANCELLED,
    };
    return statusMap[status] || status;
  }

  extractDistanceFromAddress(address) {
    if (!address) return null;
    const match = address.match(/\((.*?)\)/);
    if (match && match[1]) {
      const parts = match[1].split(",");
      return parts[0].trim();
    }
    return null;
  }

  getPrepTime(order) {
    return {
      max: order.handoverDetails?.maxTime || 0,
      min: order.handoverDetails?.minTime || 0,
      time: order.handoverDetails?.time || 0,
      step: order.handoverDetails?.stepSize || 1,
      isDelayAllowed: order.handoverDetails?.isPreparationDelayAllowed || false,
    };
  }
}
